import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import { isAuthEnabled } from "../../config/environment";
import { PendingProblemStart } from "../../auth/googleRedirect";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { signOut as signOutThunk } from "../../store/auth/authSlice";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";
import { AuthDialog, AuthReason } from "./AuthDialog";

export interface AuthRequest {
  reason: AuthReason;
  problemTitle?: string;
  /** Replayed after a Google sign-in, which reloads the app. */
  pendingProblem?: PendingProblemStart;
  /** Called when the player closes the dialog without signing in. */
  onCancel?: () => void;
}

interface AuthContextValue {
  /** Run `action` now if signed in; otherwise ask the player to sign in first, then run it. */
  requireAuth: (action: () => void, request: AuthRequest) => void;
  /** Open the dialog with nothing to run afterwards. */
  openAuthDialog: (request: AuthRequest) => void;
  signOut: () => void;
  notify: (message: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
};

interface Pending {
  action?: () => void;
  request: AuthRequest;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.auth.status);
  const statusRef = useRef(status);
  statusRef.current = status;

  // The request stays set while the dialog animates closed, so its heading doesn't change
  const [dialogRequest, setDialogRequest] = useState<AuthRequest | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const pendingRef = useRef<Pending | null>(null);
  // A request made while the cookie check is still running
  const deferredRef = useRef<Pending | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const showDialog = useCallback((pending: Pending) => {
    pendingRef.current = pending;
    setDialogRequest(pending.request);
    setIsDialogOpen(true);
    trackEvent(GA_EVENTS.AUTH_GATE_SHOWN, { reason: pending.request.reason });
  }, []);

  const requireAuth = useCallback(
    (action: () => void, request: AuthRequest) => {
      if (!isAuthEnabled() || statusRef.current === "signedIn") {
        action();
      } else if (statusRef.current === "checking") {
        deferredRef.current = { action, request };
      } else {
        showDialog({ action, request });
      }
    },
    [showDialog],
  );

  const openAuthDialog = useCallback((request: AuthRequest) => showDialog({ request }), [showDialog]);

  // Settle a request that arrived before the cookie check finished
  useEffect(() => {
    const deferred = deferredRef.current;
    if (!deferred || status === "checking") return;
    deferredRef.current = null;
    if (status === "signedIn") {
      deferred.action?.();
    } else {
      showDialog(deferred);
    }
  }, [status, showDialog]);

  const handleClose = () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setIsDialogOpen(false);
    pending?.request.onCancel?.();
  };

  const handleSignedIn = (isNewAccount: boolean) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setIsDialogOpen(false);
    setSnackbar(isNewAccount ? "Welcome! Your free account is ready." : "Signed in.");
    pending?.action?.();
  };

  const signOut = useCallback(() => {
    dispatch(signOutThunk());
    trackEvent(GA_EVENTS.SIGN_OUT);
    setSnackbar("Signed out.");
  }, [dispatch]);

  const value = useMemo(
    () => ({ requireAuth, openAuthDialog, signOut, notify: setSnackbar }),
    [requireAuth, openAuthDialog, signOut],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AuthDialog
        open={isDialogOpen}
        reason={dialogRequest?.reason ?? "menu"}
        problemTitle={dialogRequest?.problemTitle}
        pendingProblem={dialogRequest?.pendingProblem}
        onClose={handleClose}
        onSignedIn={handleSignedIn}
      />
      <Snackbar
        open={snackbar !== null}
        message={snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </AuthContext.Provider>
  );
};
